//@ts-nocheck
//@ts-ignore
/* eslint-disable */
import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNav } from 'src/onboarding/hooks/nav';

import styles from './style.module.scss';
import { t } from '@web-solutions/module-localization';
import { useUpsalePurchase } from 'core/upsale/hooks/use-upsale-purchase';
import Analytics from '@web-solutions/module-analytics';
import { useRemoteConfig } from 'core/hooks/use-remote-config';
import { getLocalizedList, getLocalizedText } from 'core/utils/localized';
import Button from 'src/onboarding/components/ui/button/Button';
import { Caption } from 'src/onboarding/components/typography/Caption/Caption';
import { ReactComponent as LoadingIcon } from '../../assets/images/loading-white.svg';
import upsaleBundle from '../../assets/images/upsale-nutrition-bundle.png';
import { Text } from 'src/onboarding/components/typography/Text/Text';
import instance from 'src/onboarding/api/interceptor';
import { useStore } from 'src/onboarding/store/store';
import SuccessPayment from '../webUpsale/components/successPaymentPopup';
import { Title } from 'src/onboarding/components/typography/Title/Title';
import PaymentFailedSheet, {
  PAYMENT_FAILED_CATEGORY,
} from 'src/onboarding/components/ui/payment-failed-sheet';
import { getEnvUrl } from 'src/onboarding/helpers/getEnvUrl';
import { applyFoodzeeAuthTokens } from 'src/onboarding/helpers/applyFoodzeeAuthTokens';
import axios from 'axios';
import upsaleEthnicity from '../../assets/images/upsale-ethnicity.png';
import { getPwaUrlForOnboarding } from 'src/onboarding/helpers/getPwaUrl';
import {
  requestHandoffCode,
  buildAppUrlWithHandoff,
} from 'src/onboarding/helpers/pwaHandoff';
import upsaleDna from '../../assets/images/upsaleDna.png';
import Cookies from 'js-cookie';
import getLocale from 'src/localization/get-locale';
import { PaymentSystem, Provider } from '@web-solutions/react-billing';
import ApplePay from 'core/payment/components/apple-pay';
import { selectAllProducts, selectPaymentSystem } from 'core/store/billing/selectors';
import { createOneClickPayment, handleErrorPurchase } from 'core/store/billing/actions';
import { extractDeclineCode } from 'core/payment/utils/get-payment-error-content';
import { PaymentErrorState } from 'core/payment/utils/payment-error-state';
import { confirmStripePaymentIntent } from 'core/utils/stripe-payment';

const images = {
  upsaleBundle,
  upsaleEthnicity,
  upsaleDna,
};

// Price line for the payment modal. Intl keeps non-USD funnels honest; the `$`
// fallback covers a currency code Intl doesn't know.
const formatUpsalePrice = (amount: number, currency: string) => {
  if (!Number.isFinite(amount) || amount <= 0) {
    return '';
  }

  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);
  } catch (e) {
    return `$${amount.toFixed(2)}`;
  }
};

const UpsaleDynamic = () => {
  const { onNext } = useNav();
  const locale = getLocale();

  const email = useStore((state) => state.email);
  const userId = useStore((state) => state.userId);
  const pageAnswers = useStore((state) => state.pageAnswers);
  const imageUrl = useStore((state) => state.imageUrl);
  const setPage = useStore((state) => state.setPage);
  const authTokens = useStore((state) => state.authTokens);
  const authTokensExpiry = useStore((state) => state.authTokensExpiry);
  const clearAuthTokens = useStore((state) => state.clearAuthTokens);
  const dispatch = useDispatch<any>();
  const paymentMethod = useSelector((state: any) => state.billing.paymentMethod);
  const purchase = useSelector((state: any) => state.billing.purchase);
  const allProducts = useSelector(selectAllProducts);
  const paymentSystem = useSelector(selectPaymentSystem);
  const orderDetails = useSelector((state: any) => state.billing.orderDetails);
  const apiAuthSource = useSelector((s: any) => s.profile?.apiAuth?.source);
  const isTruegate = paymentSystem === PaymentSystem.TRUEGATE;
  const isStripe = paymentSystem === PaymentSystem.STRIPE;

  const { upsaleDynamic, sendEthnicity, showUpsalePaymentError, redirectToPwa } = useRemoteConfig();

  const truegateUpsaleProduct = useMemo(() => {
    if (!isTruegate) return null;
    const ud: any = upsaleDynamic || {};
    const parsedAmount = parseFloat(String(ud.priceAmount).replace(/[^\d.]/g, ''));
    return {
      id: ud.truegateId || ud.recurlyId || ud.productCode || 'upsale',
      truegateId: ud.truegateId,
      productCode: ud.productCode || ud.recurlyId,
      amount: parsedAmount || 0,
      currency: (purchase?.currency || 'USD').toUpperCase(),
      isOneTimePurchase: true,
    } as any;
  }, [isTruegate, upsaleDynamic, purchase?.currency]);

  const stripeUpsaleProduct = useMemo(() => {
    if (!isStripe) return null;
    const ud: any = upsaleDynamic || {};
    const parsedAmount = parseFloat(String(ud.priceAmount).replace(/[^\d.]/g, ''));
    const currency = (purchase?.currency || 'USD').toUpperCase();

    // `'upsale'` is the last-resort label: the charge is amount-driven, so a
    // funnel config without a Stripe id still goes through — the payment just
    // can't be tied back to a product in Stripe, which makes reporting blind.
    if (!ud.stripeId && !ud.productCode && !ud.recurlyId && !ud.productId) {
      console.warn(
        '[upsale-dynamic] the offer has no stripeId / productCode / productId — '
        + `charging ${ud.priceAmount} with the placeholder product id "upsale". `
        + 'Add stripeId to the funnel config so the payment is identifiable in Stripe.',
      );
    }

    return {
      id: ud.stripeId || ud.productCode || ud.recurlyId || ud.productId || 'upsale',
      productCode: ud.productCode || ud.stripeId || ud.recurlyId || ud.productId || 'upsale',
      amount: parsedAmount || 0,
      currency,
      isOneTimePurchase: true,
      // Shown at the top of the payment modal when the user retries with another
      // card, so it's clear what is being bought: offer name + its price.
      title: ud.title,
      priceText: formatUpsalePrice(parsedAmount, currency),
    } as any;
  }, [isStripe, upsaleDynamic, purchase?.currency]);

  const upsaleProductForApplePay = useMemo(() => {
    if (
      upsaleDynamic.product &&
      typeof upsaleDynamic.product === 'object' &&
      (upsaleDynamic.product as any).currency
    ) {
      const p = upsaleDynamic.product as any;
      const parsedAmount = parseFloat(String(upsaleDynamic.priceAmount).replace(/[^\d.]/g, ''));
      return {
        ...p,
        amount: p.amount || parsedAmount || 0,
      };
    }
    const found = allProducts.find(
      (p: any) =>
        p.id === upsaleDynamic.product ||
        p.recurlyId === upsaleDynamic.recurlyId ||
        p.plan_name === upsaleDynamic.recurlyId
    );
    if (found) return found;
    const parsedAmount = parseFloat(String(upsaleDynamic.priceAmount).replace(/[^\d.]/g, ''));
    console.log('upsaleDynamic', upsaleDynamic);
    console.log('parsedAmount', parsedAmount);
    console.log('upsaleDynamic.priceAmount', upsaleDynamic.priceAmount);
    return {
      id: upsaleDynamic.recurlyId,
      amount: parsedAmount || 0,
      currency: purchase?.currency || 'usd',
    };
  }, [allProducts, upsaleDynamic, purchase]);

  const [successPayment, setSuccessPayment] = useState(false);
  const [showCheck, setShowCheck] = useState(false);
  const [failedPayment, setFailedPayment] = useState(false);
  // Provider error behind the failed charge — the sheet turns its code into a
  // human-readable reason.
  const [paymentError, setPaymentError] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [currentAttempt, setCurrentAttempt] = useState(0);
  const [errorMessages, setErrorMessages] = useState([]);
  const {
    handleUpsalePurchase,
    getLastPurchaseError,
    renderPaymentProcessor,
    showPaymentPopup,
    closePaymentPopup,
  } = useUpsalePurchase();
  // True while the current attempt came from "Try another card", so a success
  // can be attributed to the recovery flow.
  const isRetryAttemptRef = useRef(false);

  const { apiBaseUrl, isProd } = getEnvUrl();

  // QA switch, non-prod only: `?forceUpsaleFail=insufficient_funds` fails the
  // upsale charge with that decline code without involving the provider. Needed
  // because a test card that succeeds on the paywall also succeeds here, so the
  // first-charge decline can't be reproduced with test cards alone.
  //
  // Modifiers, appended after a colon:
  //   :once  — fail every attempt of the FIRST purchase only, so the retry with
  //            another card reaches the provider for real.
  //   :first — fail only the FIRST attempt of every purchase, so the retry
  //            ladder (alternativeProduct1/2) is charged for real and can
  //            succeed. This is the only way to test the ladder end-to-end.
  const forcedFail = useMemo(() => {
    if (isProd) {
      return null;
    }

    const param = new URLSearchParams(window.location.search).get('forceUpsaleFail');

    if (!param) {
      return null;
    }

    const [rawCode, modifier] = param.split(':');

    return {
      declineCode: !rawCode || rawCode === 'true' ? 'generic_decline' : rawCode,
      once: modifier === 'once',
      firstAttemptOnly: modifier === 'first',
    };
  }, [isProd]);
  // Counts user-initiated purchases, not internal auto-retries: with `:once`
  // every attempt of the first purchase fails (so the sheet always appears) and
  // the next one — the retry with another card — reaches the provider.
  const purchaseRunRef = useRef(0);

  const throwForcedFailure = (attemptIndex = 0) => {
    if (!forcedFail) {
      return;
    }

    if (forcedFail.once && purchaseRunRef.current > 1) {
      return;
    }

    if (forcedFail.firstAttemptOnly && attemptIndex > 0) {
      return;
    }

    const error: any = new Error(`Forced upsale failure: ${forcedFail.declineCode}`);
    error.code = 'card_declined';
    error.decline_code = forcedFail.declineCode;
    throw error;
  };

  // A charge that succeeded after the sheet had already been shown is a
  // recovered sale — report it separately from a first-attempt success.
  const trackRecoveredPurchase = useCallback(() => {
    if (!isRetryAttemptRef.current) {
      return;
    }
    isRetryAttemptRef.current = false;
    Analytics.trackEvent(PAYMENT_FAILED_CATEGORY, 'success', {
      upsaleID: 'upsale-dynamic',
    });
  }, []);

  // The app bootstrap dispatches its own auth() (source 'billing') as a
  // fire-and-forget on load, overwriting the 'foodzee' billing auth the quiz set
  // up. For Stripe every billing request needs source 'foodzee' or it rejects
  // with "Missing billing auth token" — which is what entering a new card here
  // would hit, because the charge on the saved method never calls createCustomer
  // and so never noticed. Whenever the source drifts away while we hold Foodzee
  // tokens, re-assert it. Same guard as on the paywall.
  useEffect(() => {
    if (!isStripe || !authTokens?.accessToken || apiAuthSource === 'hairio') {
      return;
    }

    applyFoodzeeAuthTokens(
      {
        accessToken: authTokens.accessToken,
        refreshToken: authTokens.refreshToken,
        uid: authTokens.userId || userId,
      },
      dispatch,
      { reinitBilling: false, paymentSystem }
    );
  }, [apiAuthSource, authTokens, userId, dispatch, isStripe, paymentSystem]);

  const setDnaRequestCookie = () => {
    const currentDomain = window.location.hostname;
    const domainParts = currentDomain.split('.');
    const rootDomain = domainParts.slice(-2).join('.');

    Cookies.set('dna_request', 'positive', {
      expires: 30,
      domain: `.${rootDomain}`,
      path: '/',
      secure: window.location.protocol === 'https:',
      sameSite: 'lax',
    });
  };

  // Autologin handoff into the PWA. Exchanges the funnel's accessToken for a
  // one-time handoff code and passes it to the app in the URL fragment
  // (`#handoff=<code>`); the PWA redeems it and logs the user in without an
  // email code. Best-effort — on any failure we open the app without the
  // fragment and the user hits the normal login. (Replaces the older
  // `onboarding_auth` cookie that shipped the raw tokens.)
  const openPWAWithTokens = async () => {
    if (!redirectToPwa || !authTokens) {
      return;
    }

    const pwaUrl = getPwaUrlForOnboarding();
    const code = await requestHandoffCode(authTokens.accessToken);

    window.location.href = buildAppUrlWithHandoff(pwaUrl, code);
  };

  const sendEthnicityAnswers = async () => {
    try {
      const ethnicityAnswers = Object.entries(pageAnswers).map(([key, value]) => {
        const questionText =
          //@ts-ignore
          (value.title && value.title.en) ||
          //@ts-ignore
          (typeof value.title === 'string' ? value.title : '');

        return {
          question: questionText,
          //@ts-ignore
          answer: Array.isArray(value.answer) ? value.answer.join(', ') : value.answer,
        };
      });

      const userData: any = {
        email,
      };

      if (sendEthnicity === 'onUpsale') {
        if (ethnicityAnswers.length > 0) {
          userData.ethnicity = {
            data: ethnicityAnswers,
            ...(imageUrl && { photoUrl: imageUrl }),
          };
        } else {
          userData.ethnicity = undefined;
        }
      }

      await axios.put(`${apiBaseUrl}/user/update-by-token/${userId}`, userData);
      Analytics.trackEvent('user-update-token-upsale', 'success');
    } catch (error) {
      Analytics.trackEvent('user-update-token-upsale', 'error');
      console.error('Error:', error);
    }
  };

  const sendNutritionBundleRequest = async () => {
    try {
      const payload = {
        email,
        bundle: upsaleDynamic.fileName,
        template: upsaleDynamic.letterName,
      };

      const response = await instance.post('/user/nutrition-cooking-bundle', payload);
      return response.data;
    } catch (error) {
      console.error('Error sending bundle request:', error);
    }
  };

  const sendDnaReportRequest = async () => {
    try {
      const response = await instance.post(
        '/user/dna-report',
        {},
        {
          headers: {
            Authorization: `Bearer ${authTokens.accessToken}`,
          },
        }
      );

      if (response.data) {
        setDnaRequestCookie();
      }
      return response.data;
    } catch (error) {
      console.error('Error sending DNA report request:', error);
    }
  };

  const sendReport = async () => {
    if (sendEthnicity === 'onUpsale' && upsaleDynamic.image !== 'upsaleDna') {
      await sendEthnicityAnswers();
    } else if (upsaleDynamic.image === 'upsaleDna') {
      await sendDnaReportRequest();
    } else {
      await sendNutritionBundleRequest();
    }
  };

  // Single exit for every failed upsale charge — card, Apple Pay, one-click, any
  // provider.
  //
  // `showUpsalePaymentError` is the kill-switch for the whole feature: with it
  // disabled the funnel keeps the old behaviour and carries the user on through
  // the flow without ever mentioning the failed charge.
  const showPaymentFailedSheet = (error?: any) => {
    if (!showUpsalePaymentError) {
      setLoading(false);
      sendReport().finally(() => {
        if (redirectToPwa) {
          openPWAWithTokens();
        }
        onNext();
      });
      return;
    }

    setPaymentError(error || null);
    setFailedPayment(true);
    setLoading(false);
  };

  const getProductByAttempt = (attemptIndex) => {
    switch (attemptIndex) {
      case 0:
        return {
          recurlyId: upsaleDynamic.recurlyId,
          product: upsaleDynamic.product,
          name: upsaleDynamic.upsaleName,
          attempt: 1,
        };
      case 1:
        return {
          recurlyId: upsaleDynamic.alternativeProduct1?.recurlyId,
          product: upsaleDynamic.alternativeProduct1?.product,
          name: upsaleDynamic.alternativeProduct1?.upsaleName,
          attempt: 2,
        };
      case 2:
        return {
          recurlyId: upsaleDynamic.alternativeProduct2?.recurlyId,
          product: upsaleDynamic.alternativeProduct2?.product,
          name: upsaleDynamic.alternativeProduct2?.upsaleName,
          attempt: 3,
        };
      default:
        return null;
    }
  };

  // Stripe counterpart of getProductByAttempt. The one-click call sends the
  // amount explicitly, so an alternative offer is only chargeable when the
  // config gives it its own price — otherwise we'd bill the main offer's amount
  // for a cheaper product. Such an attempt is skipped, not guessed.
  const getStripeProductByAttempt = (attemptIndex: number) => {
    const currency = stripeUpsaleProduct.currency;

    if (attemptIndex === 0) {
      return {
        id: stripeUpsaleProduct.id,
        productCode: stripeUpsaleProduct.productCode,
        amount: stripeUpsaleProduct.amount,
        currency,
        name: upsaleDynamic.upsaleName,
        description:
          upsaleDynamic.upsaleName
          || upsaleDynamic.title?.en
          || stripeUpsaleProduct.productCode,
      };
    }

    const alt =
      attemptIndex === 1 ? upsaleDynamic.alternativeProduct1 : upsaleDynamic.alternativeProduct2;

    if (!alt) {
      return null;
    }

    const amount = parseFloat(String(alt.priceAmount ?? '').replace(/[^\d.]/g, ''));

    if (!Number.isFinite(amount) || amount <= 0) {
      console.warn(
        `[upsale-dynamic] alternativeProduct${attemptIndex} has no priceAmount — skipping the Stripe retry for it`,
      );
      return null;
    }

    const id = alt.stripeId || alt.productCode || alt.recurlyId || alt.productId;

    if (!id) {
      console.warn(
        `[upsale-dynamic] alternativeProduct${attemptIndex} has no product id — skipping the Stripe retry for it`,
      );
      return null;
    }

    return {
      id,
      productCode: alt.productCode || alt.productId || id,
      amount,
      currency,
      name: alt.upsaleName || upsaleDynamic.upsaleName,
      // A downsell is the same offer at a lower price, so it inherits the main
      // offer's wording unless the config overrides it. Without this the Stripe
      // payment would be described by a raw price id.
      description:
        alt.upsaleName
        || alt.title?.en
        || upsaleDynamic.upsaleName
        || upsaleDynamic.title?.en
        || id,
    };
  };

  const attemptPurchase = async (attemptIndex, tokenId?: string) => {
    const product = getProductByAttempt(attemptIndex);

    try {
      throwForcedFailure(attemptIndex);
      const data = await handleUpsalePurchase({
        recurlyId: product.recurlyId,
        upsaleName: product.name,
        isNextStepBlocked: true,
        tokenId,
        // The payment-failed sheet reports the failure — no toast on top of it.
        noToast: true,
      });
      if (!data) {
        const errorMsg = `Payment failed for ${product.name} (attempt ${attemptIndex + 1})`;
        setErrorMessages((prev) => [...prev, errorMsg]);

        if (attemptIndex < 2 && upsaleDynamic.enableRetryMechanism) {
          const nextAttempt = attemptIndex + 1;
          setCurrentAttempt(nextAttempt);
          return await attemptPurchase(nextAttempt, tokenId);
        } else {
          throw new Error(errorMsg);
        }
      }

      Analytics.trackEvent('step', 'completed', {
        upsaleID: 'upsale-dynamic',
        result: 'upsale-dynamic_payment',
        attempt: attemptIndex + 1,
        product: product.name,
      });

      gtag('event', 'conversion', {
        send_to: 'AW-17149973324/WBLJCNvSyP0aEMym3_E_',
        value: upsaleDynamic.priceAmount,
        currency: 'USD',
        transaction_id: '',
      });

      if (sendEthnicity === 'onUpsale' && upsaleDynamic.image !== 'upsaleDna') {
        await sendEthnicityAnswers();
      } else if (upsaleDynamic.image === 'upsaleDna') {
        await sendDnaReportRequest();
      } else {
        await sendNutritionBundleRequest();
      }

      trackRecoveredPurchase();
      setSuccessPayment(true);
      setFailedPayment(false);
      setCurrentAttempt(0);
      setTimeout(() => {
        if (redirectToPwa) {
          openPWAWithTokens();
        }
        setSuccessPayment(false);
      }, 2000);

      return data;
    } catch (error: any) {
      const errorMsg = `Error in attempt ${attemptIndex + 1}: ${error}`;
      setErrorMessages((prev) => [...prev, errorMsg]);

      if (attemptIndex < 2 && upsaleDynamic.enableRetryMechanism) {
        const nextAttempt = attemptIndex + 1;
        setCurrentAttempt(nextAttempt);
        return await attemptPurchase(nextAttempt, tokenId);
      } else {
        throw error;
      }
    }
  };

  // Truegate: shared post-payment side effects (mirrors attemptPurchase success branch).
  const finishTruegatePurchase = useCallback(async () => {
    try {
      Analytics.trackEvent('step', 'completed', {
        upsaleID: 'upsale-dynamic',
        result: 'upsale-dynamic_payment',
        paymentSystem: PaymentSystem.TRUEGATE,
      });

      gtag('event', 'conversion', {
        send_to: 'AW-17149973324/WBLJCNvSyP0aEMym3_E_',
        value: upsaleDynamic.priceAmount,
        currency: 'USD',
        transaction_id: '',
      });

      if (sendEthnicity === 'onUpsale' && upsaleDynamic.image !== 'upsaleDna') {
        await sendEthnicityAnswers();
      } else if (upsaleDynamic.image === 'upsaleDna') {
        await sendDnaReportRequest();
      } else {
        await sendNutritionBundleRequest();
      }

      trackRecoveredPurchase();
      setSuccessPayment(true);
      setFailedPayment(false);
      setTimeout(() => {
        if (redirectToPwa) {
          openPWAWithTokens();
        }
        setSuccessPayment(false);
      }, 2000);
    } catch (e) {
      console.error('truegate upsale post-success error', e);
    }
  }, [upsaleDynamic, sendEthnicity, redirectToPwa, trackRecoveredPurchase]);

  const finishStripePurchase = useCallback(async () => {
    try {
      Analytics.trackEvent('step', 'completed', {
        upsaleID: 'upsale-dynamic',
        result: 'upsale-dynamic_payment',
        paymentSystem: PaymentSystem.STRIPE,
      });

      gtag('event', 'conversion', {
        send_to: 'AW-17149973324/WBLJCNvSyP0aEMym3_E_',
        value: upsaleDynamic.priceAmount,
        currency: 'USD',
        transaction_id: '',
      });

      if (sendEthnicity === 'onUpsale' && upsaleDynamic.image !== 'upsaleDna') {
        await sendEthnicityAnswers();
      } else if (upsaleDynamic.image === 'upsaleDna') {
        await sendDnaReportRequest();
      } else {
        await sendNutritionBundleRequest();
      }

      trackRecoveredPurchase();
      setSuccessPayment(true);
      setFailedPayment(false);
      setTimeout(() => {
        if (redirectToPwa) {
          openPWAWithTokens();
        }
        setSuccessPayment(false);
      }, 2000);
    } catch (e) {
      console.error('stripe upsale post-success error', e);
    }
  }, [upsaleDynamic, sendEthnicity, redirectToPwa, trackRecoveredPurchase]);

  const handlePurchaseAndProceed = async (tokenId?: string, paymentMethodId?: string) => {
    purchaseRunRef.current += 1;

    if (isTruegate) {
      if (!truegateUpsaleProduct) return;
      setLoading(true);
      try {
        throwForcedFailure();
        // Reuse subscription intent's transactionId — backend charges
        // the saved Truegate payment method without showing a modal.
        const transactionId =
          orderDetails?.products?.[truegateUpsaleProduct.productCode]?.transactionId ||
          orderDetails?.one_time_products?.[truegateUpsaleProduct.productCode]?.transactionId ||
          orderDetails?.transactionId;

        if (!transactionId) {
          throw new Error('Truegate one-click upsale: no transactionId available');
        }

        const result: any = await dispatch(
          createOneClickPayment({
            amount: truegateUpsaleProduct.amount,
            currency: truegateUpsaleProduct.currency,
            productCode: truegateUpsaleProduct.productCode || truegateUpsaleProduct.id,
            productId: truegateUpsaleProduct.id,
            transactionId,
            trigger: 'upsale-dynamic',
          }),
        );

        // TODO: handle 3DS verify_url if backend requires it (not implemented yet)
        if (result?.verify_url) {
          console.warn('Truegate one-click upsale requires 3DS — verify_url:', result.verify_url);
        }

        await finishTruegatePurchase();
      } catch (error: any) {
        // The sheet explains the reason, so the toast would only duplicate it.
        dispatch(handleErrorPurchase(error, { noToast: true }));
        showPaymentFailedSheet(error);
      } finally {
        setLoading(false);
      }
      return;
    }
    if (isStripe) {
      if (!stripeUpsaleProduct) return;

      setLoading(true);
      setCurrentAttempt(0);
      setErrorMessages([]);

      // Same downsell ladder as the legacy path: the main offer first, then the
      // configured alternatives, until one charge succeeds.
      const maxAttempt = upsaleDynamic.enableRetryMechanism ? 2 : 0;
      let lastError: any;

      for (let attempt = 0; attempt <= maxAttempt; attempt += 1) {
        const target = getStripeProductByAttempt(attempt);

        if (!target) {
          break;
        }

        setCurrentAttempt(attempt);

        try {
          throwForcedFailure(attempt);
          const result: any = await dispatch(
            createOneClickPayment({
              amount: target.amount,
              currency: target.currency,
              productCode: target.productCode || target.id,
              productId: target.id,
              description: target.description,
              trigger: attempt === 0 ? 'upsale-dynamic' : `upsale-dynamic_${attempt}attempt`,
              // Retry with another card: charge the freshly collected method and
              // keep it on file, so any further offer in this flow uses it too.
              // Without it the backend charges the customer's saved method — the
              // one that has just been declined.
              ...(paymentMethodId
                ? { paymentMethodId, setupFutureUsage: 'off_session' as const }
                : {}),
            }),
          );

          const clientSecret = result?.client_secret || result?.clientSecret;
          let status = result?.status || result?.payment_intent_status;

          if (clientSecret && result?.requires_action) {
            // Throws the Stripe error (code + decline_code) if the bank refuses
            // during 3-D Secure, which the sheet then explains.
            const paymentIntent = await confirmStripePaymentIntent(clientSecret);
            status = paymentIntent?.status || status;
          }

          // A 200 with a non-succeeded intent is still a failed charge — the
          // failure details travel in the body, not as an HTTP error.
          if (status && status !== 'succeeded') {
            throw Object.assign(new Error('Stripe upsale payment was not completed'), {
              code: result?.error?.code || result?.last_payment_error?.code || status,
              // Searched over the whole response: this wrapper replaces it, so a
              // decline code left behind here never reaches the error events.
              decline_code: extractDeclineCode(result, PaymentSystem.STRIPE),
            });
          }

          await finishStripePurchase();
          setLoading(false);
          return;
        } catch (error: any) {
          lastError = error;
          setErrorMessages((prev) => [
            ...prev,
            `Stripe attempt ${attempt + 1} failed (${target.name}): ${error?.message}`,
          ]);
        }
      }

      if (upsaleDynamic.enableRetryMechanism) {
        Analytics.trackEvent('payment', 'all_attempts_failed', {
          upsaleID: 'upsale-dynamic',
          paymentSystem: PaymentSystem.STRIPE,
          retryMechanismEnabled: true,
          place: 'upsale',
          code: lastError?.code,
          decline_code: extractDeclineCode(lastError, PaymentSystem.STRIPE),
        });
      }

      dispatch(handleErrorPurchase(lastError, { noToast: true }));
      showPaymentFailedSheet(lastError);
      setLoading(false);
      return;
    }
    if (!tokenId && !purchase?.token_id) {
      showPaymentPopup();
      return;
    }

    setLoading(true);
    setCurrentAttempt(0);
    setErrorMessages([]);
    setFailedPayment(false);

    try {
      await attemptPurchase(0, tokenId);
    } catch (finalError) {
      // The provider error carries the decline code; the thrown wrapper doesn't.
      const providerError = getLastPurchaseError() || finalError;

      Analytics.trackEvent('payment', 'all_attempts_failed', {
        upsaleID: 'upsale-dynamic',
        errors: errorMessages,
        retryMechanismEnabled: upsaleDynamic.enableRetryMechanism,
        attemptCount: currentAttempt + 1,
        place: 'upsale',
        code: (providerError as any)?.code,
        decline_code: extractDeclineCode(providerError, paymentSystem),
      });
      showPaymentFailedSheet(providerError);
    } finally {
      setLoading(false);
    }
  };

  const getButtonText = () => {
    if (loading) {
      return <LoadingIcon />;
    }
    return getLocalizedText(upsaleDynamic.buttonText, locale);
  };

  /**
   * The sheet's primary button. Where it leads depends on the state:
   *  - state 3 is a glitch on the way to the bank, so it repeats the charge on
   *    the saved card straight from this screen — no window opens;
   *  - states 1 and 4 need a different card or a bank confirmation, and only the
   *    payment window can offer those. The card entered there comes back through
   *    `handleCardCollected`, so the declined method on file isn't retried. For
   *    state 4 the bank's confirmation runs inside that window too.
   * State 2 can't happen here — the user never entered a card.
   */
  const handleTryAnotherCard = (state: PaymentErrorState) => {
    isRetryAttemptRef.current = true;
    setFailedPayment(false);

    if (state === PaymentErrorState.TEMPORARY) {
      handlePurchaseAndProceed();
      return;
    }

    showPaymentPopup();
  };

  // A payment method was tokenized in the modal — charge the offer with it.
  const handleCardCollected = async (formData: any) => {
    const paymentMethodId = formData?.payment_method_id;
    const tokenId = formData?.token_id;

    // No fresh method (e.g. a PayPal submit): retrying would charge the very
    // card that has just been declined, so let the modal finish its own flow.
    if (!paymentMethodId && !tokenId) {
      return;
    }

    closePaymentPopup();
    await handlePurchaseAndProceed(tokenId, paymentMethodId);
  };

  // "Skip offer": the offer is declined, so nothing is charged and nothing is
  // delivered — just move on through the flow.
  const handleSkipOffer = () => {
    setFailedPayment(false);
    isRetryAttemptRef.current = false;
    if (redirectToPwa) {
      openPWAWithTokens();
    }
    onNext();
  };

  // X: leave the user on the offer screen so they can read it again and retry.
  const handleCloseSheet = () => {
    setFailedPayment(false);
    isRetryAttemptRef.current = false;
  };

  const handleApplePayUpsale = async (formData: any) => {
    if (formData?.token_id || formData?.payment_method_id) {
      await handlePurchaseAndProceed(formData.token_id, formData.payment_method_id);
    }
  };

  const handleSkipClick = async () => {
    // Legacy "silent mode": funnels with showUpsalePaymentError=false hand the
    // report over even though the offer was never paid for. Kept as-is for now —
    // whether skipping should still deliver it is a product call.
    if (!showUpsalePaymentError) {
      await sendReport();
    }
    Analytics.trackEvent('skip', 'click');

    if (redirectToPwa) {
      openPWAWithTokens();
    }
    onNext();
  };

  useEffect(() => {
    if (successPayment) {
      document.body.style.overflow = 'hidden';

      const showCheckTimer = setTimeout(() => setShowCheck(true), 500);
      const nextTimer = setTimeout(() => onNext(), 1500);

      return () => {
        clearTimeout(showCheckTimer);
        clearTimeout(nextTimer);
        document.body.style.overflow = '';
      };
    } else if (failedPayment) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
  }, [successPayment, failedPayment]);

  useEffect(() => {
    if (failedPayment) {
      const timer = setTimeout(() => setLoading(false), 1000);
      return () => clearTimeout(timer);
    }
  }, [failedPayment]);

  const formatPriceInText = (text, priceAmount) => {
    if (!text || !priceAmount) return text;

    return text.replace(/\$\d+\.\d{2}/g, `$${priceAmount}`);
  };

  const onApplePayClick = useCallback(() => {
    Analytics.trackEvent('apple_pay', 'click');
  }, []);

  return (
    <>
      <main className={styles.main}>
        <div className={styles.header}>
          <h2 className={styles.headerTitle}>{getLocalizedText(upsaleDynamic.title, locale)}</h2>
          <Caption size='l' style={{ color: '#3F3F3F' }}>
            {getLocalizedText(upsaleDynamic.subTitle, locale)}
          </Caption>
          <img
            src={images[upsaleDynamic.image]}
            style={upsaleDynamic.image === 'upsaleDna' ? { transform: 'scale(1.5)' } : {}}
            className={styles.headerImg}
            alt='plans'
          />
        </div>

        <div className={styles.content}>
          {renderPaymentProcessor({
            // For Stripe the modal must price the offer itself, not the paywall
            // product the funnel config points at.
            activeProduct: isStripe ? stripeUpsaleProduct : upsaleDynamic.product,
            oneTimeRecurlyId: upsaleDynamic.recurlyId,
            // Changing the payment method should offer every method the user has,
            // wallets included: inside the Apple Pay sheet they can pick a
            // different card, which is exactly what a declined charge needs.
            isOnlyCard: false,
            onCardCollected: handleCardCollected,
          })}

          <div className={styles.usersContainer}>
            <Text size='l' semibold className={styles.users}>
              {getLocalizedText(upsaleDynamic.descriptionTitle, locale)}
            </Text>
            <Text size='m' medium style={{ color: '#8A8A8A' }}>
              {getLocalizedText(upsaleDynamic.descriptionText, locale)}
            </Text>
          </div>

          <div className={styles.youGetContainer}>
            <Title tag='h4'>{t('onboarding.upsaleNutritionBundle.youGet.title')}</Title>
            <div className={styles.youGetOptions}>
              {getLocalizedList(upsaleDynamic.whatYouGetText, locale)
                ?.filter((item) => {
                  if (upsaleDynamic.movePriceToBottom) {
                    return !item.includes('Lifetime access') && !item.includes('price');
                  }
                  return true;
                })
                ?.map((item, index) => (
                  <Text key={index} size='m' medium className={styles.youGetOption}>
                    {item}
                  </Text>
                ))}
            </div>
          </div>
        </div>
        {successPayment && (
          <SuccessPayment onClose={() => setSuccessPayment(false)} showCheck={showCheck} />
        )}

        <PaymentFailedSheet
          visible={failedPayment}
          error={paymentError}
          paymentSystem={paymentSystem}
          method={paymentMethod}
          loading={loading}
          analyticsInfo={{ upsaleID: 'upsale-dynamic' }}
          onRetry={handleTryAnotherCard}
          onSkip={handleSkipOffer}
          onClose={handleCloseSheet}
        />
      </main>
      {!failedPayment && !successPayment && (
        <footer
          className={styles.footer}
          style={
            upsaleDynamic.movePriceToBottom && upsaleDynamic.bottomText ? { bottom: '16px' } : {}
          }
        >
          {!isTruegate && paymentMethod === 'applepay' ? (
            <Provider>
              <ApplePay
                onApplePayClick={onApplePayClick}
                className={styles.appleButton}
                activeProduct={upsaleProductForApplePay}
                onApplePaySubmit={handleApplePayUpsale}
              />
            </Provider>
          ) : (
            <Button
              disabled={loading}
              title={getButtonText()}
              onClick={() => handlePurchaseAndProceed()}
            />
          )}
        </footer>
      )}
      <div
        className={styles.skip}
        style={
          upsaleDynamic.movePriceToBottom && upsaleDynamic.bottomText
            ? { bottom: '-100px' }
            : { bottom: '-60px' }
        }
      >
        {upsaleDynamic.movePriceToBottom && upsaleDynamic.bottomText && (
          <div className={styles.bottomTextContainer}>
            <p className={styles.bottomText}>
              {formatPriceInText(
                getLocalizedText(upsaleDynamic.bottomText, locale),
                upsaleDynamic.priceAmount,
              )}{' '}
              <span className={styles.privacy}>
                <a onClick={() => setPage('privacy-policy')}>
                  {t('onboarding.personalPlan.plan.privacy')}
                </a>{' '}
                and{' '}
                <a onClick={() => setPage('terms-of-use')}>
                  {t('onboarding.personalPlan.plan.terms')}
                </a>
              </span>
            </p>
          </div>
        )}
        <Button
          type='secondary'
          title={t('onboarding.upsaleNutritionBundle.skip')}
          onClick={handleSkipClick}
        />
      </div>

    </>
  );
};

export default UpsaleDynamic;
